import supabase from '@/api/supabase';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { useQuery } from '@tanstack/react-query';
import { endOfWeek, format, startOfWeek, subWeeks } from 'date-fns';
import { TrendingDown, TrendingUp } from 'lucide-react';
import * as React from 'react';
import { Cell, Label, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

// Define colors for the pie chart
const COLORS = {
  employee1: 'hsl(var(--chart-1))', // Coral red
  employee2: 'hsl(var(--chart-2))', // Teal
  employee3: 'hsl(var(--chart-3))', // Dark blue
  employee4: 'hsl(var(--chart-4))', // Yellow
  employee5: 'hsl(var(--chart-5))', // Orange
  employee6: 'hsl(280, 65%, 65%)', // Purple
  employee7: 'hsl(150, 60%, 40%)', // Forest green
  employee8: 'hsl(340, 65%, 65%)', // Rose pink
  employee9: 'hsl(200, 70%, 50%)', // Sky blue
  employee10: 'hsl(45, 70%, 50%)', // Golden yellow
  employee11: 'hsl(170, 50%, 45%)', // Seafoam green
  employee12: 'hsl(320, 50%, 45%)', // Magenta
  others: 'hsl(215, 16%, 47%)', // Neutral gray
} as const;

interface EmployeeData {
  total: number;
  roles: Record<string, number>;
  managers: string[];
  owners: string[];
}

interface IncomeData {
  chartData: Array<{
    name: string;
    value: number;
    fill: string;
  }>;
  currentWeekTotal: number;
  lastWeekTotal: number;
  percentageChange: number;
}

export const EmployeeStaticCard = () => {
  // Get current week dates
  const currentDate = new Date();
  const startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
  const endDate = endOfWeek(currentDate, { weekStartsOn: 1 });

  // State for tooltip visibility and position
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  // Handle click outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setActiveIndex(null);
      }
    };

    if (activeIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeIndex]);

  // Get last week dates for comparison
  const lastWeekDate = subWeeks(currentDate, 1);
  const lastWeekStart = startOfWeek(lastWeekDate, { weekStartsOn: 1 });
  const lastWeekEnd = endOfWeek(lastWeekDate, { weekStartsOn: 1 });

  // Format date range for display
  const dateRangeText = React.useMemo(
    () => `${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd')}`,
    [startDate, endDate]
  );

  // Fetch employee count data
  const { data: employeeCount } = useQuery<EmployeeData>({
    queryKey: ['employeeCount'],
    queryFn: async () => {
      const { data, error } = await supabase.from('employees').select('empRole, empName');

      if (error) throw error;

      // Initialize role counters and name lists
      const roles: Record<string, number> = {};
      const managers: string[] = [];
      const owners: string[] = [];

      // Process each employee's roles
      data.forEach(emp => {
        // Handle roles array
        const empRoles = Array.isArray(emp.empRole) ? emp.empRole : [emp.empRole];
        empRoles.forEach(role => {
          roles[role] = (roles[role] || 0) + 1;

          // Track managers and owners
          if (role.toLowerCase() === 'manager') {
            managers.push(emp.empName);
          } else if (role.toLowerCase() === 'owner') {
            owners.push(emp.empName);
          }
        });
      });

      return {
        total: data.length,
        roles,
        managers,
        owners,
      };
    },
  });

  // Fetch current and last week income data
  const { data: incomeData } = useQuery<IncomeData>({
    queryKey: ['weeklyIncome', startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {
      // Fetch current week data
      const { data: currentWeekData, error: currentError } = await supabase
        .from('taskManager_history')
        .select('income, name')
        .gte('checkinTime', format(startDate, 'yyyy-MM-dd'))
        .lte('checkinTime', format(endDate, 'yyyy-MM-dd'));

      if (currentError) throw currentError;

      // Fetch last week data
      const { data: lastWeekData, error: lastError } = await supabase
        .from('taskManager_history')
        .select('income')
        .gte('checkinTime', format(lastWeekStart, 'yyyy-MM-dd'))
        .lte('checkinTime', format(lastWeekEnd, 'yyyy-MM-dd'));

      if (lastError) throw lastError;

      // Process current week data for pie chart
      const employeeIncomes = currentWeekData.reduce((acc: Record<string, number>, curr) => {
        acc[curr.name] = (acc[curr.name] || 0) + curr.income;
        return acc;
      }, {});

      const currentWeekTotal = Object.values(employeeIncomes).reduce(
        (sum, value) => sum + value,
        0
      );

      const chartData = Object.entries(employeeIncomes)
        .sort((a, b) => b[1] - a[1])
        .map(([name, value], index) => ({
          name,
          value,
          fill: COLORS[`employee${index + 1}` as keyof typeof COLORS] || COLORS.others,
          total: currentWeekTotal, // Add total to each data point for tooltip
        }));

      const lastWeekTotal = lastWeekData.reduce((sum, item) => sum + item.income, 0);

      // Calculate percentage change
      const percentageChange =
        lastWeekTotal === 0 ? 0 : ((currentWeekTotal - lastWeekTotal) / lastWeekTotal) * 100;

      return {
        chartData,
        currentWeekTotal,
        lastWeekTotal,
        percentageChange,
      };
    },
  });

  return (
    <div className="grid gap-1 sm:gap-3 md:grid-cols-2 lg:grid-cols-3 pb-1 sm:pb-3">
      {/* Total Staff Card */}
      <Card className="lg:col-span-1 col-span-2 border-none ">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-md font-semibold">Total Staff</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{employeeCount?.total || 0}</div>
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground mt-2">
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-primary" />
                Owner:
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {employeeCount?.owners.map(owner => (
                  <Badge key={owner} variant="secondary" className="text-xs">
                    {owner}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-primary" />
                Manager:
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {employeeCount?.managers.map(manager => (
                  <Badge key={manager} variant="secondary" className="text-xs">
                    {manager}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
              {Object.entries(employeeCount?.roles || {})
                .filter(([role]) => !['owner', 'manager'].includes(role.toLowerCase()))
                .map(([role, count]) => (
                  <div key={role} className="flex justify-between items-center">
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 rounded-full bg-primary" />
                      <span className="capitalize">{role}:</span>
                    </div>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Income Card */}
      <Card className="col-span-2 border-none">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
          <div>
            <CardTitle className="text-md font-semibold">Weekly Income</CardTitle>
            <CardDescription>{dateRangeText}</CardDescription>
          </div>
          <span className="text-2xl font-bold">
            ${incomeData?.currentWeekTotal?.toLocaleString() || '0'}
          </span>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="relative">
            <ChartContainer className="mx-auto aspect-square max-h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    trigger="click"
                    allowEscapeViewBox={{ x: true, y: true }}
                    wrapperStyle={{ outline: 'none' }}
                    cursor={{ stroke: 'var(--primary)', strokeWidth: 2 }}
                    content={({ active, payload }) => {
                      if (active && payload?.length && activeIndex !== null) {
                        const data = payload[0].payload;
                        return (
                          <div ref={tooltipRef}>
                            <ChartTooltip>
                              <ChartTooltipContent>
                                <div style={{ color: data.fill }}>
                                  <div className="font-semibold">{data.name}</div>
                                  <div>${data.value.toLocaleString()}</div>
                                </div>
                              </ChartTooltipContent>
                            </ChartTooltip>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Pie
                    data={incomeData?.chartData || []}
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    isAnimationActive={false}
                    activeIndex={activeIndex !== null ? [activeIndex] : []}
                    onClick={(_, index, e) => {
                      setActiveIndex(prev => (prev === index ? null : index));
                      // Trigger tooltip on touch devices
                      if (e) {
                        const event = e as unknown as TouchEvent;
                        if (event.touches) {
                          event.preventDefault();
                        }
                      }
                    }}
                  >
                    {incomeData?.chartData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                    <Label
                      content={({ viewBox }) => {
                        if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                          return (
                            <text
                              x={viewBox.cx}
                              y={viewBox.cy}
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              <tspan
                                x={viewBox.cx}
                                y={viewBox.cy}
                                className="fill-foreground text-2xl font-bold"
                              >
                                ${incomeData?.currentWeekTotal?.toLocaleString()}
                              </tspan>
                              <tspan
                                x={viewBox.cx}
                                y={(viewBox.cy || 0) + 24}
                                className="text-sm fill-muted-foreground"
                              >
                                This Week
                              </tspan>
                            </text>
                          );
                        }
                        return null;
                      }}
                    />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {incomeData?.chartData?.map(entry => (
              <div key={entry.name} className="flex items-center text-xs">
                <div className="w-3 h-3 rounded mr-1" style={{ backgroundColor: entry.fill }} />
                <span className="truncate">{entry.name}</span>
                <span className="ml-auto font-medium">${entry.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </CardContent>
        <CardFooter className="flex items-center gap-2 text-sm">
          {incomeData?.percentageChange !== undefined && (
            <div className="flex items-center gap-2 font-medium">
              {incomeData.percentageChange > 0 ? (
                <>
                  <span className="text-green-500">
                    +{Math.abs(incomeData.percentageChange).toFixed(1)}%
                  </span>
                  <TrendingUp className="h-4 w-4 text-green-500" />
                </>
              ) : (
                <>
                  <span className="text-red-500">
                    -{Math.abs(incomeData.percentageChange).toFixed(1)}%
                  </span>
                  <TrendingDown className="h-4 w-4 text-red-500" />
                </>
              )}
              <span className="text-muted-foreground">from last week</span>
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};
